/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/mesh/feature 层；
 * 2. 负责定义编辑态/创建态预览体对象的统一抽象基类；
 * 3. 该对象用于承载“预览 mesh + 特征原型 + 草稿定义”的稳定关联。
 *
 * 文件职责：
 * 1. 继承现有 FeaturePreviewMesh，保留黄色轮廓描边显示能力；
 * 2. 显式保存所属特征原型与草稿定义，避免预览阶段再去依赖旧正式结果体；
 * 3. 为后续所有特征的预览体对象提供统一基类。
 */

import type * as THREE from 'three';
import type { FeatureDefinition } from '../../feature/definition/FeatureDefinition';
import type { FeaturePrototype } from '../../feature/prototype/FeaturePrototype';
import { FeaturePreviewMesh } from './FeaturePreviewMesh';

/**
 * 特征预览体抽象基类。
 */
export abstract class FeaturePreviewObject<
    TPrototype extends FeaturePrototype = FeaturePrototype,
    TDraftDefinition extends FeatureDefinition = FeatureDefinition
> extends FeaturePreviewMesh {
    /** 所属特征原型。 */
    private readonly prototype: TPrototype;
    /** 当前草稿定义。 */
    private readonly draftDefinition: TDraftDefinition;

    /**
     * @param input 输入参数。
     */
    protected constructor(input: {
        prototype: TPrototype;
        draftDefinition: TDraftDefinition;
        meshData: MeshData;
        geometry: THREE.BufferGeometry;
        material: THREE.Material | THREE.Material[];
    }) {
        super({
            meshData: input.meshData,
            geometry: input.geometry,
            material: input.material,
        });
        this.prototype = input.prototype;
        this.draftDefinition = input.draftDefinition;
    }

    /**
     * 获取所属特征原型。
     *
     * @returns 特征原型。
     */
    public getFeaturePrototype(): TPrototype {
        return this.prototype;
    }

    /**
     * 获取当前草稿定义。
     *
     * @returns 草稿定义对象。
     */
    public getDraftDefinition(): TDraftDefinition {
        return this.draftDefinition;
    }
}
